import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import chauffeurService from '../../services/chauffeurService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { getFileUrl } from '../../config';
import { FaUser, FaSave, FaTimes, FaUpload, FaTrash, FaDownload, FaRobot } from 'react-icons/fa';
import './ChauffeurForm.css';
import { getChauffeurDocuments, uploadChauffeurDocument, deleteChauffeurDocument, downloadChauffeurDocument } from '../../services/chauffeurService';
import { useI18n } from '../../i18n/I18nContext';
import { useUser } from '../../context/UserContext';

function ChauffeurForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { t } = useI18n();
  const { user } = useUser();

  const [formData, setFormData] = useState({
    enterpriseName: '',
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    confirmPassword: '',
    telephone: '',
    licenseNumber: '',
    disponible: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [requiredDocFiles, setRequiredDocFiles] = useState({});
  // Add drag state
  const [dragOverDoc, setDragOverDoc] = useState(null);
  const [isDraggingGlobal, setIsDraggingGlobal] = useState(false);

  const REQUIRED_DOCUMENTS = useMemo(() => ([
    { key: 'PERMIS', label: t('chauffeurForm.documents.types.PERMIS') },
    { key: 'CARTE_CHAUFFEUR', label: t('chauffeurForm.documents.types.CARTE_CHAUFFEUR') },
    { key: 'CARTE_CHRONO', label: t('chauffeurForm.documents.types.CARTE_CHRONO') },
    { key: 'CARTE_FCOS', label: t('chauffeurForm.documents.types.CARTE_FCOS') },
  ]), [t]);

  useEffect(() => {
    if (isEditMode) {
      const fetchChauffeur = async () => {
        try {
          setLoading(true);
          const chauffeur = await chauffeurService.getDriverById(id);
          setFormData({
            ...chauffeur,
            password: '', // Don't show password in edit mode
            confirmPassword: '' // Don't show confirm password in edit mode
          });
          setError(null);
          try {
            const docs = await getChauffeurDocuments(id);
            setExistingDocuments(docs || []);
          } catch (_) {
            // ignore doc load errors
          }
        } catch (err) {
          console.error('Error fetching chauffeur:', err);
          setError(t('chauffeurForm.errors.loadDetailsError'));
        } finally {
          setLoading(false);
        }
      };

      fetchChauffeur();
    }
  }, [id, isEditMode, t]);

  // Pre-fill enterprise name for new drivers from current user's transporteur profile
  useEffect(() => {
    if (!isEditMode && user && user.enterpriseName && !formData.enterpriseName) {
      setFormData(prev => ({
        ...prev,
        enterpriseName: user.enterpriseName
      }));
    }
  }, [isEditMode, user, formData.enterpriseName]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!isEditMode && (!formData.password || formData.password.length < 6)) {
      setError(t('chauffeurForm.errors.passwordMinLength') || 'Le mot de passe doit comporter au moins 6 caractères');
      return false;
    }
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError(t('chauffeurForm.errors.passwordMismatch'));
      return false;
    }
    if (formData.password && formData.password.length < 6) {
      setError(t('chauffeurForm.errors.passwordMinLength') || 'Le mot de passe doit comporter au moins 6 caractères');
      return false;
    }
    return true;
  };

  const handleOcrExtraction = async (file) => {
    setOcrLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/agent/planning/ocr/stage', fd, {
        headers: { 'Content-Type': undefined },
      });
      const data = res.data || {};

      // Update form with AI extracted data
      setFormData(prev => ({
        ...prev,
        firstname: data.firstName || data.first_name || prev.firstname,
        lastname: data.lastName || data.last_name || prev.lastname,
        licenseNumber: data.permitNumber || data.permit_number || prev.licenseNumber
      }));
    } catch (err) {
      console.error("AI OCR error:", err);
      // Fail silently for OCR, user can still fill manually
    } finally {
      setOcrLoading(false);
    }
  };

  const handleRequiredDocUpload = async (docKey, file) => {
    if (!file) return;

    // Trigger AI OCR if this is the driving license
    if (docKey === 'PERMIS') {
      handleOcrExtraction(file);
    }

    const config = REQUIRED_DOCUMENTS.find(d => d.key === docKey);
    const MAX_FILE_MB = 25;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_FILE_MB) {
      setError(t('chauffeurForm.errors.fileSizeError', { max: MAX_FILE_MB, name: file.name, size: sizeMb.toFixed(1) }));
      return;
    }

    // During creation, we don't yet have a driver ID. Queue the file in local state
    // and actually upload it right after the driver is created.
    if (!isEditMode) {
      setRequiredDocFiles(prev => ({
        ...prev,
        [docKey]: {
          file,
          label: config ? config.label : file.name,
        },
      }));
      return;
    }

    if (!id) return;

    setIsUploadingDocs(true);
    setError(null);
    try {
      await uploadChauffeurDocument(id, file, {
        name: config ? config.label : file.name,
        type: docKey
      });
      const refreshed = await getChauffeurDocuments(id);
      setExistingDocuments(refreshed || []);
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(t('chauffeurForm.errors.uploadDocError'));
    } finally {
      setIsUploadingDocs(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Remove confirmPassword and password from dataToSend for standard update
      const { confirmPassword, password, ...dataToSend } = formData;

      if (isEditMode) {
        await chauffeurService.updateDriver(id, dataToSend);
        if (password && password.trim()) {
          await chauffeurService.resetDriverPassword(id, password.trim());
        }
        // Navigate to chauffeur view page after edit
        navigate(`/chauffeurs/view/${id}`);
        return;
      } else {
        const created = await chauffeurService.addDriver({ ...dataToSend, password });

        // After creating the driver, upload any queued required documents
        const entries = Object.entries(requiredDocFiles || {});
        if (entries.length) {
          setIsUploadingDocs(true);
          try {
            for (const [docKey, payload] of entries) {
              if (!payload || !payload.file) continue;
              const label = payload.label || (REQUIRED_DOCUMENTS.find(d => d.key === docKey)?.label) || payload.file.name;
              await uploadChauffeurDocument(created.id, payload.file, {
                name: label,
                type: docKey,
              });
            }
          } finally {
            setIsUploadingDocs(false);
            setRequiredDocFiles({});
          }
        }

        // Navigate to chauffeur view page after full setup
        navigate(`/chauffeurs/view/${created.id}`);
        return;
      }
    } catch (err) {
      console.error('Error saving chauffeur:', err);
      const rawMsg = (err && err.message) ? err.message : t('chauffeurForm.errors.saveFailed');
      let displayMsg = rawMsg;

      if (rawMsg.toLowerCase().includes('license number') || rawMsg.toLowerCase().includes('licence')) {
        displayMsg = t('chauffeurForm.errors.licenseDuplicate');
      } else if (rawMsg.toLowerCase().includes('limit')) {
        displayMsg = t('chauffeurs.errors.limitReached');
      }

      setError(displayMsg);
      setLoading(false);
      setIsUploadingDocs(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <DashboardLayout>
        <div className="container mt-4">
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="chauffeur-form-container">
        <div className={`chauffeur-form-card ${isDraggingGlobal ? 'drag-active-global' : ''}`}
          onDragEnter={(e) => {
            e.preventDefault();
            setIsDraggingGlobal(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDraggingGlobal(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setIsDraggingGlobal(false);
              setDragOverDoc(null);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingGlobal(false);
            setDragOverDoc(null);
          }}
        >
          <div className="chauffeur-form-header">
            <h2 className="chauffeur-form-title">
              <FaUser className="profile-icon" />
              {isEditMode ? t('chauffeurForm.title.edit') : t('chauffeurForm.title.create')}
            </h2>
          </div>

          {error && (
            <div className="alert alert-danger alert-custom" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-stack">
              <div className="form-section">
                <h3 className="section-title">{t('chauffeurForm.sections.personal')}</h3>
                <div className="field-grid">
                  <div className="field-item">
                    <label className="form-label" htmlFor="firstname">{t('chauffeurForm.fields.firstname')}</label>
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="firstname"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleChange}
                        required
                        placeholder={t('chauffeurForm.placeholders.firstname')}
                      />
                      <label htmlFor="firstname">{t('chauffeurForm.fields.firstname')}</label>
                    </div>
                  </div>
                  <div className="field-item">
                    <label className="form-label" htmlFor="lastname">{t('chauffeurForm.fields.lastname')}</label>
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="lastname"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleChange}
                        required
                        placeholder={t('chauffeurForm.placeholders.lastname')}
                      />
                      <label htmlFor="lastname">{t('chauffeurForm.fields.lastname')}</label>
                    </div>
                  </div>
                  <div className="field-item">
                    <label className="form-label" htmlFor="email">{t('chauffeurForm.fields.email')}</label>
                    <div className="form-floating">
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={t('chauffeurForm.placeholders.email')}
                      />
                      <label htmlFor="email">{t('chauffeurForm.fields.email')}</label>
                    </div>
                  </div>
                  <div className="field-item">
                    <label className="form-label" htmlFor="telephone">{t('chauffeurForm.fields.telephone')}</label>
                    <div className="form-floating">
                      <input
                        type="tel"
                        className="form-control"
                        id="telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        required
                        placeholder={t('chauffeurForm.placeholders.telephone')}
                      />
                      <label htmlFor="telephone">{t('chauffeurForm.fields.telephone')}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">{t('chauffeurForm.sections.professional')}</h3>
                <div className="field-grid">
                  <div className="field-item">
                    <label className="form-label" htmlFor="enterpriseName">{t('chauffeurForm.fields.enterpriseName')}</label>
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="enterpriseName"
                        name="enterpriseName"
                        value={formData.enterpriseName}
                        onChange={handleChange}
                        required
                        placeholder={t('chauffeurForm.placeholders.enterpriseName')}
                      />
                      <label htmlFor="enterpriseName">{t('chauffeurForm.fields.enterpriseName')}</label>
                    </div>
                  </div>
                  <div className="field-item">
                    <label className="form-label" htmlFor="licenseNumber">{t('chauffeurForm.fields.licenseNumber')}</label>
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="licenseNumber"
                        name="licenseNumber"
                        value={formData.licenseNumber}
                        onChange={handleChange}
                        required
                        placeholder={t('chauffeurForm.placeholders.licenseNumber')}
                      />
                      <label htmlFor="licenseNumber">{t('chauffeurForm.fields.licenseNumber')}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">
                  {t('chauffeurForm.sections.security')}
                  {isEditMode && <span className="text-muted fw-normal fs-6 ms-2">({t('chauffeurForm.optionalInEdit') || 'Laisser vide pour ne pas modifier'})</span>}
                </h3>
                <div className="field-grid">
                  <div className="field-item">
                    <label className="form-label" htmlFor="password">
                      {isEditMode ? (t('chauffeurForm.fields.newPassword') || 'Nouveau mot de passe') : t('chauffeurForm.fields.password')}
                    </label>
                    <div className="form-floating">
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        name="password"
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={handleChange}
                        required={!isEditMode}
                        minLength={6}
                        placeholder={isEditMode ? (t('chauffeurForm.placeholders.newPassword') || 'Nouveau mot de passe') : t('chauffeurForm.placeholders.password')}
                      />
                      <label htmlFor="password">
                        {isEditMode ? (t('chauffeurForm.fields.newPassword') || 'Nouveau mot de passe') : t('chauffeurForm.fields.password')}
                      </label>
                    </div>
                  </div>
                  <div className="field-item">
                    <label className="form-label" htmlFor="confirmPassword">
                      {isEditMode ? (t('chauffeurForm.fields.confirmNewPassword') || 'Confirmer le nouveau mot de passe') : t('chauffeurForm.fields.confirmPassword')}
                    </label>
                    <div className="form-floating">
                      <input
                        type="password"
                        className="form-control"
                        id="confirmPassword"
                        name="confirmPassword"
                        autoComplete="new-password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required={!isEditMode || Boolean(formData.password)}
                        minLength={6}
                        placeholder={isEditMode ? (t('chauffeurForm.placeholders.confirmNewPassword') || 'Confirmer le mot de passe') : t('chauffeurForm.placeholders.confirmPassword')}
                      />
                      <label htmlFor="confirmPassword">
                        {isEditMode ? (t('chauffeurForm.fields.confirmNewPassword') || 'Confirmer le nouveau mot de passe') : t('chauffeurForm.fields.confirmPassword')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="form-section">
              <h3 className="section-title">{t('chauffeurForm.sections.documents')}</h3>
              <div className="doc-card-grid">
                {REQUIRED_DOCUMENTS.map((docDef) => {
                  const current = (existingDocuments || []).find(
                    (d) => d.type === docDef.key || d.name === docDef.label
                  );
                  const queued = requiredDocFiles[docDef.key];

                  return (
                    <div
                      key={docDef.key}
                      className={`doc-card ${dragOverDoc === docDef.key ? 'drag-over' : ''}`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverDoc(docDef.key);
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverDoc(docDef.key);
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                          setDragOverDoc(null);
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverDoc(null);
                        setIsDraggingGlobal(false);
                        const file = e.dataTransfer.files && e.dataTransfer.files[0];
                        if (file) {
                          handleRequiredDocUpload(docDef.key, file);
                        }
                      }}
                    >

                      <div className="doc-card-header">
                        <div className="doc-card-icon">
                          <FaDownload />
                        </div>
                        <div className="doc-card-title">{docDef.label}</div>
                      </div>
                      <div className="doc-card-body">
                        <div className="doc-card-status">
                          {current ? (
                            <>
                              <span className="status-pill status-pill--ok">
                                {t('chauffeurForm.documents.statusUploaded')}
                              </span>
                              <span className="doc-card-filename">
                                {current.name || current.fileUrl}
                              </span>
                            </>
                          ) : queued ? (
                            <>
                              <span className="status-pill status-pill--ok">
                                {t('chauffeurForm.documents.statusQueued')}
                              </span>
                              <span className="doc-card-filename">
                                {queued.file?.name}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="status-pill status-pill--missing">
                                {t('chauffeurForm.documents.statusMissing')}
                              </span>
                              <span className="doc-card-helper">
                                {t('chauffeurForm.documents.noDoc')}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="doc-card-actions">
                          <div className="doc-card-upload">
                            <input
                              id={`doc-input-${docDef.key}`}
                              type="file"
                              className="d-none"
                              accept=".pdf,image/*"
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                handleRequiredDocUpload(docDef.key, file);
                                e.target.value = '';
                              }}
                            />
                            <label
                              htmlFor={`doc-input-${docDef.key}`}
                              className="doc-upload-chip"
                            >
                              <FaUpload className="me-1" />
                              {(current || queued)
                                ? t('chauffeurForm.documents.replace')
                                : t('chauffeurForm.documents.upload')}
                            </label>
                            {docDef.key === 'PERMIS' && ocrLoading && (
                              <div className="ms-2 d-inline-flex align-items-center text-primary small fw-bold fade-in">
                                <span className="spinner-border spinner-border-sm me-2" style={{ width: '0.8rem', height: '0.8rem' }}></span>
                                <FaRobot className="me-1" /> IA...
                              </div>
                            )}
                          </div>
                          {current && (
                            <div className="doc-card-secondary-actions">
                              <a
                                className="doc-icon-button"
                                href={getFileUrl(current.fileUrl)}
                                target="_blank"
                                rel="noreferrer"
                                title="Open document"
                              >
                                <i className="bi bi-box-arrow-up-right" />
                              </a>
                              <button
                                type="button"
                                className="doc-icon-button doc-icon-button-danger"
                                title="Remove document"
                                onClick={async () => {
                                  try {
                                    await deleteChauffeurDocument(current.id);
                                    setExistingDocuments((prev) =>
                                      prev.filter((d) => d.id !== current.id)
                                    );
                                  } catch (_) { }
                                }}
                              >
                                <i className="bi bi-trash" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="doc-drop-hint">
                          {t('chauffeurForm.documents.dropHint')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Other / legacy documents (non-required types) – hidden unless there are some */}
              {isEditMode && (() => {
                const requiredKeys = new Set(REQUIRED_DOCUMENTS.map((d) => d.key));
                const otherDocs = (existingDocuments || []).filter(
                  (doc) => !requiredKeys.has(doc.type)
                );
                if (otherDocs.length === 0) return null;
                return (
                  <div className="mt-4">
                    {otherDocs.map((doc) => (
                      <div key={doc.id} className="card p-2 mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-semibold">{doc.name || doc.fileUrl}</div>
                            <div className="text-muted small">{doc.type}</div>
                          </div>
                          <div>
                            <a
                              className="btn btn-outline-secondary btn-sm me-2"
                              href={getFileUrl(doc.fileUrl)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <FaDownload className="me-1" /> View
                            </a>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={async () => {
                                try {
                                  await deleteChauffeurDocument(doc.id);
                                  setExistingDocuments((prev) =>
                                    prev.filter((d) => d.id !== doc.id)
                                  );
                                } catch (_) { }
                              }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => navigate('/chauffeurs')}
              >
                <FaTimes className="btn-icon" /> {t('chauffeurForm.actions.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-save"
                disabled={loading || isUploadingDocs}
              >
                {loading || isUploadingDocs ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {isUploadingDocs ? t('chauffeurForm.actions.uploading') : t('chauffeurForm.actions.saving')}
                  </>
                ) : (
                  <>
                    <FaSave className="btn-icon" /> {t('chauffeurForm.actions.save')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ChauffeurForm;